import { useEffect, useState } from 'react';
import { NOTE_MAX_LENGTH, type MapObject, type StickyNote } from '@etown/shared';
import { Modal } from '../../components/Modal';
import { useOffice } from '../office/OfficeContext';
import './objects.css';

const NOTE_COLORS = ['yellow', 'coral', 'teal', 'lavender'] as const;

/**
 * Stickyboard: Notizen sind von allen editierbar (Last-write-wins).
 * Layout in einem Raster; x/y der Notiz kodiert die Sortierposition.
 */
export function StickyboardModal({ object, onClose }: { object: MapObject; onClose: () => void }) {
  const { socket } = useOffice();
  const [notes, setNotes] = useState<StickyNote[]>([]);

  useEffect(() => {
    socket.emit('note:list', { objectId: object.id }, (res) => {
      if (res.ok) setNotes(res.data.notes);
    });
    const onCreated = (note: StickyNote): void => {
      if (note.objectId === object.id) setNotes((prev) => (prev.some((n) => n.id === note.id) ? prev : [...prev, note]));
    };
    const onUpdated = (note: StickyNote): void => {
      setNotes((prev) => prev.map((n) => (n.id === note.id ? note : n)));
    };
    const onDeleted = ({ id }: { id: string }): void => {
      setNotes((prev) => prev.filter((n) => n.id !== id));
    };
    socket.on('note:created', onCreated);
    socket.on('note:updated', onUpdated);
    socket.on('note:deleted', onDeleted);
    return () => {
      socket.off('note:created', onCreated);
      socket.off('note:updated', onUpdated);
      socket.off('note:deleted', onDeleted);
    };
  }, [socket, object.id]);

  const addNote = (): void => {
    const color = NOTE_COLORS[notes.length % NOTE_COLORS.length]!;
    socket.emit('note:create', { objectId: object.id, body: '', color, x: 0, y: 0 }, (res) => {
      if (res.ok) setNotes((prev) => [...prev, res.data]);
    });
  };

  const saveBody = (note: StickyNote, body: string): void => {
    if (body === note.body) return;
    socket.emit('note:update', { id: note.id, body }, (res) => {
      if (res.ok) setNotes((prev) => prev.map((n) => (n.id === note.id ? res.data : n)));
    });
  };

  const cycleColor = (note: StickyNote): void => {
    const index = NOTE_COLORS.indexOf(note.color as (typeof NOTE_COLORS)[number]);
    const color = NOTE_COLORS[(index + 1) % NOTE_COLORS.length]!;
    socket.emit('note:update', { id: note.id, color }, (res) => {
      if (res.ok) setNotes((prev) => prev.map((n) => (n.id === note.id ? res.data : n)));
    });
  };

  const remove = (note: StickyNote): void => {
    socket.emit('note:delete', { id: note.id }, (res) => {
      if (res.ok) setNotes((prev) => prev.filter((n) => n.id !== note.id));
    });
  };

  return (
    <Modal title="Sticky Notes" onClose={onClose} wide>
      <div className="stickyboard">
        {notes.map((note) => (
          <article key={note.id} className={`note note--${note.color}`}>
            <textarea
              className="note__body"
              defaultValue={note.body}
              maxLength={NOTE_MAX_LENGTH}
              placeholder="Notiz…"
              onBlur={(e) => saveBody(note, e.target.value)}
            />
            <footer className="note__footer">
              <button onClick={() => cycleColor(note)} title="Farbe wechseln" aria-label="Farbe wechseln">
                🎨
              </button>
              <button onClick={() => remove(note)} title="Löschen" aria-label="Notiz löschen">
                🗑️
              </button>
            </footer>
          </article>
        ))}
        <button className="stickyboard__add" onClick={addNote}>
          + Neue Notiz
        </button>
      </div>
    </Modal>
  );
}
